import type { ReactElement } from 'react';
import { FUEL_WEIGHT_PENALTY_FACTOR } from '../../../domain/economics';
import { SEAT_CLASSES, type AircraftType, type CabinMetrics } from '../../../domain/models';
import { InfoPopover } from '../common/InfoPopover';
import {
  CLASS_LABELS,
  comfortTone,
  formatInt,
  formatKg,
  formatKgHr,
  formatKgSigned,
  formatMultiplier,
  formatOneDecimal,
  formatSigned,
} from './cabinFormat';

export interface MetricsPanelProps {
  readonly type: AircraftType;
  readonly metrics: CabinMetrics;
}

interface WeightRow {
  readonly key: string;
  readonly label: string;
  readonly kg: number;
}

function MassSection({ type, metrics }: MetricsPanelProps): ReactElement {
  const { weightBreakdown, weightDeltaKg } = metrics;
  const configuredDryMass = type.operatingEmptyWeightKg + weightDeltaKg;
  const rows: readonly WeightRow[] = [
    { key: 'seats', label: 'Seats', kg: weightBreakdown.seatsKg },
    { key: 'galleys', label: 'Galleys', kg: weightBreakdown.galleysKg },
    { key: 'wifi', label: 'Wi-Fi hardware', kg: weightBreakdown.wifiKg },
    { key: 'power', label: 'In-seat power', kg: weightBreakdown.powerKg },
  ];
  const maxRow = Math.max(1, ...rows.map((row) => row.kg));

  return (
    <section className="ce-metric-block" aria-labelledby="ce-mass-title">
      <h4 id="ce-mass-title" className="ce-metric-block__title">
        Configuration mass
      </h4>

      <ul className="ce-weights">
        {rows.map((row) => (
          <li key={row.key} className="ce-weights__row">
            <span className="ce-weights__label">{row.label}</span>
            <span className="ce-weights__bar" aria-hidden="true">
              <span className="ce-weights__fill" style={{ width: `${(row.kg / maxRow) * 100}%` }} />
            </span>
            <span className="ce-weights__value">{formatKg(row.kg)}</span>
          </li>
        ))}
      </ul>

      <dl className="ce-stats">
        <div className="ce-stats__row">
          <dt>Cabin fit-out total</dt>
          <dd>{formatKg(weightBreakdown.totalKg)}</dd>
        </div>
        <div className="ce-stats__row">
          <dt>Base dry mass (type OEW)</dt>
          <dd>{formatKg(type.operatingEmptyWeightKg)}</dd>
        </div>
        <div className="ce-stats__row">
          <dt>Change vs reference cabin</dt>
          <dd>{formatKgSigned(weightDeltaKg)}</dd>
        </div>
        <div className="ce-stats__row ce-stats__row--total">
          <dt>Configured dry mass</dt>
          <dd>{formatKg(configuredDryMass)}</dd>
        </div>
      </dl>
    </section>
  );
}

function FuelSection({ type, metrics }: MetricsPanelProps): ReactElement {
  const delta = metrics.fuelBurnDeltaKgHr;
  const tone = delta > 0.05 ? 'worse' : delta < -0.05 ? 'better' : 'neutral';
  return (
    <section className="ce-metric-block" aria-labelledby="ce-fuel-title">
      <h4 id="ce-fuel-title" className="ce-metric-block__title">
        Fuel burn
        <InfoPopover topic="massFuelBurn" />
      </h4>
      <p className="ce-fuel__headline">
        <span className="ce-fuel__value">{formatKgHr(metrics.effectiveFuelBurnKgHr)}</span>
        <span className={`ce-fuel__delta ce-fuel__delta--${tone}`}>
          {formatSigned(delta, 1)} kg/hr
        </span>
      </p>
      <p className="ce-metric-block__note">
        Base {formatKgHr(type.baseFuelBurnKgHr)} + ({formatKgSigned(metrics.weightDeltaKg)} ×{' '}
        {FUEL_WEIGHT_PENALTY_FACTOR} kg/hr per kg)
      </p>
    </section>
  );
}

function ComfortSection({ metrics }: Pick<MetricsPanelProps, 'metrics'>): ReactElement {
  return (
    <section className="ce-metric-block" aria-labelledby="ce-comfort-title">
      <h4 id="ce-comfort-title" className="ce-metric-block__title">
        Comfort &amp; price tolerance
        <InfoPopover topic="comfortMultiplier" />
      </h4>
      <table className="ce-table">
        <thead>
          <tr>
            <th scope="col">Class</th>
            <th scope="col" className="ce-table__num">
              Seats
            </th>
            <th scope="col">Comfort</th>
            <th scope="col" className="ce-table__num">
              Fare tolerance
            </th>
          </tr>
        </thead>
        <tbody>
          {SEAT_CLASSES.map((seatClass) => {
            const seats = metrics.seatsByClass[seatClass];
            const comfort = metrics.comfortByClass[seatClass];
            const installed = seats > 0;
            return (
              <tr key={seatClass}>
                <th scope="row">
                  <span className="ce-legend__swatch" data-seat-class={seatClass} aria-hidden="true" />
                  {CLASS_LABELS[seatClass]}
                </th>
                <td className="ce-table__num">{formatInt(seats)}</td>
                <td>
                  {installed ? (
                    <span className="ce-meter" data-tone={comfortTone(comfort)}>
                      <span
                        className="ce-meter__track"
                        role="meter"
                        aria-label={`${CLASS_LABELS[seatClass]} comfort`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={comfort}
                      >
                        <span className="ce-meter__fill" style={{ width: `${comfort}%` }} />
                      </span>
                      <span className="ce-meter__value">{formatOneDecimal(comfort)}</span>
                    </span>
                  ) : (
                    <span className="ce-muted">No seats</span>
                  )}
                </td>
                <td className="ce-table__num">
                  {installed ? formatMultiplier(metrics.priceToleranceByClass[seatClass]) : '–'}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Cabin average</th>
            <td className="ce-table__num">{formatInt(metrics.totalSeats)}</td>
            <td>
              <span className="ce-meter__value">{formatOneDecimal(metrics.comfortScore)}</span>
            </td>
            <td className="ce-table__num">{formatMultiplier(metrics.priceToleranceMultiplier)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

function CrewSection({ type, metrics }: MetricsPanelProps): ReactElement {
  return (
    <section className="ce-metric-block" aria-labelledby="ce-crew-title">
      <h4 id="ce-crew-title" className="ce-metric-block__title">
        Cabin crew
      </h4>
      <p className="ce-crew__headline">
        <span className="ce-crew__value">{formatInt(metrics.requiredCabinCrew)}</span>
        <span className="ce-crew__label">flight attendants required</span>
      </p>
      <p className="ce-metric-block__note">
        For {formatInt(metrics.totalSeats)} passengers: 1 per {formatInt(type.crew.seatsPerCabinCrew)} seats,
        minimum {formatInt(type.crew.minCabinCrew)}.
      </p>
    </section>
  );
}

/** Live numbers derived from the draft cabin by the Step 3 domain math. */
export function MetricsPanel({ type, metrics }: MetricsPanelProps): ReactElement {
  return (
    <section className="ce-card" aria-labelledby="ce-metrics-title">
      <h3 id="ce-metrics-title" className="ce-card__title">
        Live metrics
      </h3>
      <MassSection type={type} metrics={metrics} />
      <FuelSection type={type} metrics={metrics} />
      <ComfortSection metrics={metrics} />
      <CrewSection type={type} metrics={metrics} />
    </section>
  );
}
